import {Box, Dialog, DialogContent, Grid, useTheme} from "@mui/material";
import {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {
    AdvanceBookingEntityRequest,
    AdvanceBookingEntityResponse,
    AlertEntityResponse,
    CapacityUtilizationEntityRequest,
    CapacityUtilizationEntityResponse,
    CompetitorsBenchmarksEntityRequest,
    CompetitorsBenchmarksEntityResponse,
    FormAlertModel,
    FormSearchAlertModel,
    PricingErrorsEntityRequest,
    PricingErrorsEntityResponse,
    RpdEntityRequest,
    RpdEntityResponse,
    UtilizationAvailabilityEntityRequest,
    UtilizationAvailabilityEntityResponse,
} from "../../../api/types/alert";
import {
    DialogInterface,
    FormDialog,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE,
} from "../../../api/types/generic";
import BootstrapDialogTitle from "../../../common/components/BootstrapDialogTitle";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import AlertCreation from "../AlertCreation";
import SearchFilters from "../../../common/components/SearchFilters";
import {useRecoilState} from "recoil";
import {alertsAtom, initialFormAlertModel, schedulingAtom, unitAtom,} from "../../../state/alertRecoil";
import {AgGridReact} from "ag-grid-react";
import Loading from "../../../common/components/Loading";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams,} from "ag-grid-community";
import BasicToolbar from "../../../common/components/BasicToolbar";
import {useAlertsRepository} from "../../../api/repositories/AlertsRepository";
import {fromEnumToFe, fromGenericBeToGenericFe, onGridReady, useActionColDef,} from "../../../utils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import DialogComponent from "../../../common/components/DialogComponent";
import ActionsPopover from "../../../common/components/ActionsPopover";
import {useTypesRepository} from "../../../api/repositories/TypesRepository";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../../constants";
import {useAlertFormUtils} from "./useAlertFormUtils";
import {validationSchema} from "../AlertFormUtils";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import i18n from "i18next";

const AlertTable = () => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //USE RECOIL ATOM
    const [alertsList, setAlertsList] = useRecoilState(alertsAtom);
    const [schedulingList, setSchedulingList] = useRecoilState(schedulingAtom);
    const [, setUnitList] = useRecoilState(unitAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState<
        SelectElementModelFE[]
    >(commercialSegmentListAtom);

    // Use State
    const [formDialog, setFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);

    //USE CUSTOM HOOK
    const {putAlert, postAlert, deleteAlert, getAlertPaged} =
        useAlertsRepository();
    const {getAlertsType, getSchedulingType, getUnitType} =
        useTypesRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();

    // AgGrid Local
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    const handleGetAlertPage = () => {
        gridRef.current!.api.refreshServerSide({purge: true});
    };

    // Use Theme
    const theme = useTheme();

    //START FORM SECTION
    const form = useForm<FormAlertModel>({
        defaultValues: initialFormAlertModel(t, legalEntityOptions, dealerOption),
        resolver: yupResolver(
            validationSchema(t),
            {},
            {
                rawValues: true,
            }
        ),
    });
    //END FORM SECTION

    // Use Alert Utils
    const {buildFormFromEntity, buildEntityFromForm} = useAlertFormUtils(form);

    const getStaticContents = async () => {
        try {
            const alertsType = await getAlertsType();
            const alertsList: SelectElementModelFE[] = fromEnumToFe(alertsType);
            setAlertsList(alertsList);

            const schedulingType = await getSchedulingType();
            const schedulingList: SelectElementModelFE[] =
                fromEnumToFe(schedulingType);
            setSchedulingList(
                schedulingList.sort((a, b) => a.label.localeCompare(b.label))
            );

            const unitsType = await getUnitType();
            const unitsList: SelectElementModelFE[] = fromEnumToFe(unitsType);
            setUnitList(unitsList);

            const commercialSegmentsData = await getCommercialSegments();
            const commercialSegmentList: SelectElementModelFE[] =
                fromGenericBeToGenericFe(commercialSegmentsData);
            setCommercialSegmentList(commercialSegmentList);
        } catch (e) {
        }
    };

    const [testFilters, setTestFilters] = useState<FormSearchAlertModel>({
        description: "",
        alertType: null,
        schedulingType: null,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    // Use Effect
    useEffect(() => {
        getStaticContents();
        // eslint-disable-next-line
    }, []);

    const handleTriggerFilters = (entries: FormSearchAlertModel) => {
        const hardcodedFilter = Object.entries(entries).reduce(
            (acc, [key, value]) => ({
                ...acc,
                [key]: {type: "contains", filter: value},
            }),
            {}
        );

        gridRef.current?.api.paginationGoToFirstPage();
        gridRef.current?.api.setFilterModel(hardcodedFilter);
        gridRef.current?.api.onFilterChanged();
    };

    useEffect(() => {
        setTimeout(() => {
            handleTriggerFilters(testFilters);
        }, 500);
    }, [i18n.resolvedLanguage]);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        setTestFilters({
            description: "",
            alertType: null,
            schedulingType: null,
            dealers: [],
            legalEntities: []
        });
        handleTriggerFilters({
            description: "",
            alertType: null,
            schedulingType: null,
            dealers: [],
            legalEntities: []
        });
    };

    const handleConfirmSave = async () => {
        const values = form.getValues();
        if (values.uuid) {
            const newAlert:
                | CapacityUtilizationEntityRequest
                | UtilizationAvailabilityEntityRequest
                | CompetitorsBenchmarksEntityRequest
                | RpdEntityRequest
                | PricingErrorsEntityRequest
                | AdvanceBookingEntityRequest
                | null = buildEntityFromForm(everyLegalEntitiesWithoutAll, dealerFilteredOptions);

            try {
                if (newAlert) {
                    await putAlert({
                        ...newAlert,
                        uuid: values.uuid,
                    });
                    // reset form
                    form.reset(initialFormAlertModel(t, legalEntityOptions, dealerOption));
                    setFormDialog(null);
                    // get lista alert
                    handleGetAlertPage();
                    dispatchGlobalData(SET_SNACKBAR.SUCCESS);
                }
            } catch (e) {
            }
        } else {
            const newAlert:
                | CapacityUtilizationEntityRequest
                | UtilizationAvailabilityEntityRequest
                | CompetitorsBenchmarksEntityRequest
                | RpdEntityRequest
                | PricingErrorsEntityRequest
                | AdvanceBookingEntityRequest
                | null = buildEntityFromForm(everyLegalEntitiesWithoutAll, dealerFilteredOptions);
            try {
                if (newAlert) {
                    await postAlert(newAlert);
                    // reset form
                    form.reset(initialFormAlertModel(t, legalEntityOptions, dealerOption));
                    setFormDialog(null);
                    // get lista alert
                    handleGetAlertPage();
                    dispatchGlobalData(SET_SNACKBAR.SUCCESS);
                }
            } catch (e) {
            }
        }
    };

    const handleClickViewAlert = (
        alert:
            | CapacityUtilizationEntityResponse
            | UtilizationAvailabilityEntityResponse
            | CompetitorsBenchmarksEntityResponse
            | RpdEntityResponse
            | PricingErrorsEntityResponse
            | AdvanceBookingEntityResponse
    ) => {
        if (alert) {
            //TO DO TRADUZIONE DA ALERTENTITY A FORM PER MANDARI I CAMPI IN READ ONLY
            const result: FormAlertModel = buildFormFromEntity(
                alert,
                commercialSegmentList,
                t
            );
            form.reset(result); // asynchronously reset your form values
            setFormDialog({
                open: true,
                onClickConfirm: () => {
                },
                readonly: true,
            });
        }
    };

    const handleClickPutAlert = (
        alert:
            | CapacityUtilizationEntityResponse
            | UtilizationAvailabilityEntityResponse
            | CompetitorsBenchmarksEntityResponse
            | RpdEntityResponse
            | PricingErrorsEntityResponse
            | AdvanceBookingEntityResponse
    ) => {
        if (alert) {
            const result: FormAlertModel = buildFormFromEntity(
                alert,
                commercialSegmentList,
                t
            );
            form.reset(result); // asynchronously reset your form values
            setFormDialog({
                open: true,
                onClickConfirm: form.handleSubmit(handleConfirmSave),
                readonly: false,
            });
        }
    };

    const handleConfirmDelete = async (uuid: string) => {
        try {
            await deleteAlert(uuid);
            setDialog(null);
            form.reset(initialFormAlertModel(t, legalEntityOptions, dealerOption));
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);

            // get lista rental period
            handleGetAlertPage();
        } catch (e) {
        }
    };

    const viewButtonClicked = (
        data:
            | CapacityUtilizationEntityResponse
            | UtilizationAvailabilityEntityResponse
            | CompetitorsBenchmarksEntityResponse
            | RpdEntityResponse
            | PricingErrorsEntityResponse
            | AdvanceBookingEntityResponse
    ) => {
        handleClickViewAlert(data);
    };

    const updateButtonClicked = (
        data:
            | CapacityUtilizationEntityResponse
            | UtilizationAvailabilityEntityResponse
            | CompetitorsBenchmarksEntityResponse
            | RpdEntityResponse
            | PricingErrorsEntityResponse
            | AdvanceBookingEntityResponse
    ) => {
        handleClickPutAlert(data);
    };

    const deleteButtonClicked = (data: AlertEntityResponse) => {
        setDialog({
            open: true,
            message: t("pricing:alert.dialogContents.removeAlert"),
            onClickConfirm: () => handleConfirmDelete(data.uuid),
        });
    };

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchAlertModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce(
                    (acc, [key, value]) => ({
                        ...acc,
                        [key]: value?.filter || "",
                    }),
                    testFilters
                );

                const alertPaged = await getAlertPaged({
                    ...filter,
                    sort:
                        sort && order
                            ? `${sort === "alertType" ? "type" : sort},${order}`
                            : "",
                    size: gridRef?.current?.api?.paginationGetPageSize() || 10,
                    page: gridRef?.current?.api?.paginationGetCurrentPage() || 0,
                });
                if (alertPaged.totalElements === 0) {
                    gridRef.current!.api.showNoRowsOverlay();
                } else {
                    gridRef.current!.api.hideOverlay();
                }

                params.success({
                    rowData: alertPaged.content,
                    rowCount: alertPaged.totalElements,
                });
            },
        };
    };

    const filters: GenericComponentModel[] = [
        {
            name: "description",
            value: testFilters.description as string,
            label: `${t("pricing:alert.table.labels.name")}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, description: value}),
            options: [],
            type: "TextField",
        },
        {
            name: "alertType",
            value: testFilters.alertType,
            label: `${t("pricing:alert.labels.alertType")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, alertType: value}),
            options: alertsList,
            type: "Autocomplete",
        },
        {
            name: "scheduleId",
            value: testFilters.schedulingType,
            label: `${t("pricing:alert.labels.scheduleType")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, schedulingType: value}),
            options: schedulingList,
            type: "Autocomplete",
        },
    ];

    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "legalEntities",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOption || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_ALERT";
                        });
                    })
                        ? {
                            onClick: () =>
                                setFormDialog({
                                    open: true,
                                    onClickConfirm: form.handleSubmit(handleConfirmSave),
                                    readonly: false,
                                }),
                        }
                        : undefined
                }
            />
        ),
    };

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            field: "legalEntities",
            headerName: t("pricing:peaks.table.headers.legalEntities"),
            headerTooltip: t("pricing:upsellingRate.table.headers.legalEntities"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => {
              if (params.data.legalEntities) {
                return t('translation:legalEntities.'.concat(params.data.legalEntities))
              } else if (params.data.legalEntity){
                return t('translation:legalEntities.'.concat(params.data.legalEntity))
              } else {
                return ''
              }
            },
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "dealers",
            headerName: t("pricing:peaks.table.headers.dealers"),
            headerTooltip: t("pricing:upsellingRate.table.headers.dealers"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => {
              if (params.data.dealers) {
                return t('translation:dealers.'.concat(params.data.dealers))
              } else if (params.data.dealer){
                return t('translation:dealers.'.concat(params.data.dealer))
              } else {
                return ''
              }
            },
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "description",
            headerName: t("pricing:alert.table.labels.name"),
            headerTooltip: t("pricing:alert.table.labels.name"),
            flex: 1,
            suppressSizeToFit: true,
            filter: "text",
        },
        {
            field: "alertType",
            headerName: t("pricing:alert.table.labels.alertType"),
            headerTooltip: t("pricing:alert.table.labels.alertType"),
            flex: 1,
            suppressSizeToFit: true,
            cellRenderer: (params: ICellRendererParams) =>
                alertsList.find((s) => s.code === params.data.type)?.label,
            filter: "text",
        },
        {
            field: "schedulingType",
            headerName: t("pricing:alert.table.labels.schedule"),
            headerTooltip: t("pricing:alert.table.labels.schedule"),
            flex: 1,
            suppressSizeToFit: true,
            cellRenderer: (params: ICellRendererParams) =>
                schedulingList.find((s) => s.code === params.data.schedulingType)
                    ?.label,
            filter: "text",
            sortable: false,
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    params={params}
                    view={{
                        action: viewButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_ALERT";
                            });
                        }),
                    }}
                    update={{
                        action: updateButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_ALERT";
                            });
                        }),
                    }}
                    del={{
                        action: deleteButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_ALERT";
                            });
                        }),
                    }}
                />
            ),
        },
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_ALERT.name")}/>
            {formDialog?.open && (
                <Dialog open={formDialog.open} maxWidth={"xl"} fullWidth>
                    <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={() => {
                            form.reset(initialFormAlertModel(t, legalEntityOptions, dealerOption));
                            setFormDialog(null);
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:alert.title")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                        <FormProvider {...form}>
                            <AlertCreation
                                setFormDialog={setFormDialog}
                                handleConfirmSave={handleConfirmSave}
                                readonly={formDialog.readonly}
                            />
                        </FormProvider>
                    </DialogContent>
                </Dialog>
            )}
            {/* <CardContainer> */}
            <SearchFilters
                filtersOutOfCollapse={filters}
                filtersInCollapse={filtersInCollapse}
                handleClickSearch={() =>
                    handleTriggerFilters({
                        description: testFilters.description,
                        alertType:
                            (
                                testFilters.alertType as SelectElementModelFE
                            )?.code?.toString() || "",
                        schedulingType:
                            (
                                testFilters.schedulingType as SelectElementModelFE
                            )?.code?.toString() || "",
                        legalEntities:
                            (testFilters.legalEntities as SelectElementModelFE)?.code === "all"
                                ? optionsFiltered
                                : (testFilters.legalEntities as SelectElementModelFE)?.code as string,
                        dealers:
                            (testFilters.dealers as SelectElementModelFE)?.code === "all"
                                ? dealerFilteredOptions
                                : (testFilters.dealers as SelectElementModelFE)?.code as string,
                    })
                }
                handleClickResetSearch={handleTriggerResetFilters}
            />
            {/* </CardContainer> */}
            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={() =>
                            onGridReady({
                                initialSortColId: "description",
                                gridRef,
                                createDatasource,
                            })
                        }
                    />
                </Grid>
            </Grid>
            <Loading/>

            {dialog?.open && (
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => {
                        setDialog(null);
                        form.reset(initialFormAlertModel(t, legalEntityOptions, dealerOption));
                    }}
                    open={dialog.open}
                    message={dialog.message}
                />
            )}
        </Box>
    );
};

export default AlertTable;
